<template>
  <MainFormLayout layout="two-column">
    <div class="mb-20">
      <h1
        id="reviewerInformationTitle"
        class="font-bitter text-3xl font-bold">
        Reviewers
      </h1>
    </div>
    <RecommendReviewers
      :next="next"
      :canDisplayErrors="canDisplayErrors" />
    <div
      id="transparent_peer_review"
      class="mb-20">
      <div id="pnlReviewTypeQuestionSelector">
        <h2
          class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8"
          id="titleTPR">
          Transparent peer review
        </h2>

        <div
          id="pnlTPRType"
          class="mb-4">
          <FormRadio
            data-testid="choose-transparent-peer-review"
            v-model="transparentPeerReview"
            name="pnlTPRType"
            input-id="rdoAgreeTPR"
            labelText="I choose transparent peer review"
            :value="TransparentPeerReviewType.Agree"
            @custom-change="onagreeTPRChanged"
          />
          <FormRadio
            data-testid="choose-not-transparent-peer-review"
            v-model="transparentPeerReview"
            name="pnlTPRType"
            input-id="rdoDeclineTPR"
            labelText="I do not choose transparent peer review"
            :value="TransparentPeerReviewType.Decline"
            @custom-change="onagreeTPRChanged"
          />
        </div>
        <InlineWarningMessage
          v-if="canDisplayErrors && transparentPeerReview === 'Unknown'"
          errorMessage="You must select an option for transparent peer review"
          errorTestId="transparent-peer-review-option"
          class="mb-4"
        />
        <p>
          To support increased transparency, <i>Chemical Science</i> offers authors the option of
          <a
            class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
            href="https://www.rsc.org/journals-books-databases/journal-authors-reviewers/processes-policies/#peerreview"
            target="_blank"
            id="lnk-reviewEditorial-reviewPolicy"
          >
            transparent peer review
          </a>.
          If authors choose this option, the reviewers’ comments, authors’ response and editor’s decision letter for all
          versions
          of the manuscript are published alongside the article under an Open Access
          <a
            class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            id="lnk-reviewEditorial-creativeCommons"
          >
            Creative Commons CC-BY license</a>.
          Reviewers remain anonymous unless they choose to sign their report.
        </p>
      </div>
    </div>
    <div
      id="editorial_office"
      class="mb-20">
      <h2
        class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8"
        id="2-associate-editors">
        Associate Editor
      </h2>
      <div
        id="pnlRadioList"
        class="mb-8">
        <FormRadio
          data-testid="select-preferred-associate-editor"
          v-model="editorialSelectionMode"
          name="rdoEdOffQuestSelect"
          input-id="rdoEdOffQuestSelect"
          labelText="I would like to select my preferred Associate Editors."
          :value="EditorialOfficeSelectionMode.AuthorDecided"/>
        <FormRadio
          v-model="editorialSelectionMode"
          name="rdoEdOffQuestRSC"
          input-id="rdoEdOffQuestRSC"
          labelText="I am happy for the editorial team to select the most appropriate Associate Editor."
          :value="EditorialOfficeSelectionMode.RscDecide"
        />

        <InlineWarningMessage
          v-if="shouldShowAssociateEditorError"
          errorMessage="You must select two Associate Editors"
          errorTestId="associate-editor-selection"
          class="mt-4"
        />
      </div>
      <SelectEditor
        v-if="showEditorialOffices"
        v-model:selectedEditors="selectedEditors"
      />
    </div>
    <div>
      <PageValidationErrors
        v-if="showErrors"
        :failed-rules="failedRules"
        variant="error" />
      <ButtonComponent
        :label="nextText"
        variant="primary"
        data-testid="go-to-final-review-page"
        id="reviewEditorialNextButton"
        @customClick="next"
      />
    </div>
  </MainFormLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'
import router from '../routes'

import RecommendReviewers from '../components/reviewer/RecommendReviewers.vue'
import SelectEditor from '../components/editorial/SelectEditor.vue'
import PageValidationErrors from '../components/common/PageValidationErrors.vue'
import ButtonComponent from '../components/ui-components/ButtonComponent.vue'
import FormRadio from '../components/ui-components/FormRadio.vue'
import InlineWarningMessage from '../components/common/InlineWarningMessage.vue'
import MainFormLayout from '../layouts/MainFormLayout.vue'
import { useEditorialManagement } from '@/composables/useEditorialManagement'
import { trackEvent } from '@/services/gtm'
import validateReviewEditorialView from '../services/validation/reviewEditorial'
import {
  EditorialOfficeSelectionMode,
  TransparentPeerReviewType,
} from '@/types'

const store = useStore()

const canDisplayErrors = ref(false)

const {
  editorialSelectionMode,
  showEditorialOffices,
  selectedEditors,
  verifyResult,
  failedRules,
  canProgress,
  showErrors,
  shouldShowAssociateEditorError,
} = useEditorialManagement(canDisplayErrors)

const agreeTPR = ref(TransparentPeerReviewType.Agree)
const declineTPR = ref(TransparentPeerReviewType.Decline)
const transparentPeerReview = ref(store.state.activeSubmission.transparentPeerReview)

function onagreeTPRChanged(event: Event) {
  const targetId = (event.target as HTMLElement).id
  transparentPeerReview.value
    = targetId === 'rdoAgreeTPR' ? agreeTPR.value : declineTPR.value

  store.dispatch('setTPRType', transparentPeerReview.value)
}
const nextText = ref(store.state.finalReviewVisited ? 'Update and review' : 'Next')

const next = async () => {
  await validateReviewEditorialView(store)
  trackEvent({
    event: 'ReviewEditorialValidationWarnings',
    category: 'Review Editorial Validation Warnings',
    action: 'click',
    reviewEditorialValidationWarnings: verifyResult.value?.errors ?? [],
  })

  if (canProgress.value) {
    router.push({ name: 'final-review' })
  }
  else {
    canDisplayErrors.value = true
  }
}
</script>
